import { useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { uploadLineItemFile } from "../../../../redux/slices/invoicesSlice";

interface LineItemFileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  lineItemId: string;
  lineItemDescription: string;
  existingFiles: any[];
  onUploadSuccess?: (file: any) => void;
}

const MAX_FILES_PER_LINE_ITEM = 10;

const LineItemFileUploadDialog = ({
  open,
  onClose,
  invoiceId,
  lineItemId,
  lineItemDescription,
  existingFiles,
}: LineItemFileUploadDialogProps) => {
  const dispatch = useAppDispatch();
  const { uploadFileStatus } = useAppSelector((state) => state.invoices);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = MAX_FILES_PER_LINE_ITEM - (existingFiles?.length || 0);

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > 50 * 1024 * 1024) {
      return `"${file.name}" exceeds 50MB limit`;
    }
    return null;
  }, []);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const totalAfterAdd = selectedFiles.length + fileArray.length;

      if (totalAfterAdd > remainingSlots) {
        return;
      }

      const validFiles = fileArray.filter((file) => !validateFile(file));
      setSelectedFiles((prev) => [...prev, ...validFiles].slice(0, remainingSlots));
    },
    [selectedFiles.length, remainingSlots, validateFile],
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(event.target.files);
      event.target.value = "";
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    if (event.dataTransfer.files) {
      addFiles(event.dataTransfer.files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleUpload = async () => {
    for (const file of selectedFiles) {
      await dispatch(
        uploadLineItemFile({
          file,
          invoiceId,
          lineItemId,
        }),
      ).unwrap();
    }
    setSelectedFiles([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 0 },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Upload Files
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Line item: <strong>{lineItemDescription || "Untitled line item"}</strong>
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          {existingFiles?.length || 0} of {MAX_FILES_PER_LINE_ITEM} files used
          {remainingSlots > 0 ? ` (${remainingSlots} remaining)` : ""}
        </Typography>

        {/* Drop zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dragOver ? "primary.main" : "grey.300",
            borderRadius: 0,
            p: 4,
            textAlign: "center",
            cursor: remainingSlots > 0 ? "pointer" : "not-allowed",
            bgcolor: dragOver ? "action.hover" : "background.paper",
            transition: "all 0.2s",
            opacity: remainingSlots > 0 ? 1 : 0.5,
            mb: 2,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleFileSelect}
            disabled={remainingSlots <= 0}
          />
          <CloudUploadIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {remainingSlots > 0
              ? "Drag & drop files here, or click to browse"
              : "Maximum 10 files reached"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Any file type, up to 50MB each
          </Typography>
        </Box>

        {/* Selected files list */}
        {selectedFiles.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Files to upload ({selectedFiles.length})
            </Typography>
            <List dense disablePadding>
              {selectedFiles.map((file, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => removeFile(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <InsertDriveFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* Existing files */}
        {existingFiles?.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
              Existing files ({existingFiles.length})
            </Typography>
            <List dense disablePadding>
              {existingFiles.map((file: any) => (
                <ListItem key={file.id}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <InsertDriveFileIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.fileName}
                    secondary={formatFileSize(file.fileSize)}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {uploadFileStatus === "loading" && (
          <LinearProgress sx={{ mt: 2 }} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={selectedFiles.length === 0 || uploadFileStatus === "loading"}
        >
          Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LineItemFileUploadDialog;
